import { useEffect, useMemo, useRef, useState } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { UNITS, PRODUCT_CATEGORIES, ROLE_LABELS } from "../lib/constants";
import { toCSV, parseCSV, downloadCSV } from "../lib/csv";
import { parseXLSX, downloadXLSXTemplate } from "../lib/xlsx";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Building2, FlaskConical, UserCog, X, Search, AlertTriangle, RotateCcw, Shield, Download, Upload, FileSpreadsheet } from "lucide-react";
import { PermissionsManager } from "../components/PermissionsManager";
import { CompanySettings } from "../components/CompanySettings";

const TABS = [
  { id: "customers", label: "Müşteriler", icon: Building2 },
  { id: "products", label: "Ürünler", icon: FlaskConical },
  { id: "users", label: "Kullanıcılar", icon: UserCog, adminOnly: true },
  { id: "permissions", label: "Erişim Yetkileri", icon: Shield, adminOnly: true },
  { id: "company", label: "Firma Bilgisi", icon: Building2, adminOnly: true },
];

export default function Management() {
  const { user } = useAuth();
  const [tab, setTab] = useState("customers");
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(null); // {type, data}
  const [confirm, setConfirm] = useState(null); // {type, item}
  const [query, setQuery] = useState("");
  const [view, setView] = useState("active"); // active | archived

  const loadAll = async () => {
    try {
      const [c, p] = await Promise.all([api.get("/customers"), api.get("/products")]);
      setCustomers(c.data); setProducts(p.data);
      if (user?.role === "admin") {
        const u = await api.get("/users");
        setUsers(u.data);
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { setQuery(""); setView("active"); }, [tab]);

  const visibleTabs = TABS.filter((t) => !t.adminOnly || user?.role === "admin");

  const doDelete = async () => {
    const { item } = confirm;
    const base = { customers: "/customers", products: "/products", users: "/users" }[tab];
    try {
      const { data } = await api.delete(`${base}/${item.id}`);
      toast.success(data?.detail || "İşlem tamam");
      setConfirm(null);
      loadAll();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const reactivate = async (item) => {
    const base = tab === "customers" ? "/customers" : "/products";
    try {
      await api.put(`${base}/${item.id}`, { ...item, is_active: true });
      toast.success("Yeniden aktifleştirildi");
      loadAll();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const fileRef = useRef(null);
  const CSV_COLS = {
    customers: [["company_name", "Firma Adı"], ["customer_code", "Müşteri Kodu"], ["contact_name", "Yetkili Kişi"], ["phone", "Telefon"], ["email", "E-posta"], ["address", "Adres"], ["note", "Açıklama"], ["tax_no", "Vergi No"], ["qr_link", "Kare Kod Link"]],
    products: [["product_code", "Ürün Kodu"], ["product_name", "Ürün Adı"], ["category", "Kategori"], ["unit", "Birim"], ["description", "Açıklama"]],
  };
  const TEMPLATE_EXAMPLES = {
    customers: [
      { company_name: "Örnek Kimya A.Ş.", customer_code: "M-001", contact_name: "Ahmet Yılmaz", phone: "0532 000 00 00", email: "info@ornek.com", address: "Nilüfer / Bursa", note: "", tax_no: "1234567890", qr_link: "" },
    ],
    products: [
      { product_code: "FC104", product_name: "Konsantre Zemin Temizleme Kimyasalı", category: "Genel Temizlik", unit: "Litre", description: "Örnek açıklama" },
      { product_code: "KM-204", product_name: "Sülfürik Asit %98", category: "Asidik", unit: "Litre", description: "" },
    ],
  };
  const downloadTemplate = () => {
    downloadXLSXTemplate(CSV_COLS[tab], `${tab === "customers" ? "musteri" : "urun"}_sablonu.xlsx`, TEMPLATE_EXAMPLES[tab]);
    toast.success("Örnek Excel şablonu indirildi");
  };
  const exportCSV = () => {
    const cols = CSV_COLS[tab];
    const data = tab === "customers" ? customers : products;
    downloadCSV(toCSV(cols, data), `${tab}_${new Date().toISOString().slice(0, 10)}.csv`);
    toast.success(`${data.length} kayıt dışa aktarıldı`);
  };
  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const isExcel = /\.(xlsx|xls)$/i.test(file.name);
      const rows = isExcel ? await parseXLSX(file, CSV_COLS[tab]) : parseCSV(await file.text(), CSV_COLS[tab]);
      if (!rows.length) { toast.error("Dosyada veri bulunamadı"); return; }
      const base = tab === "customers" ? "/customers/import" : "/products/import";
      const { data } = await api.post(base, { rows });
      const extra = data.errors?.length ? ` · ${data.errors.length} satır atlandı` : "";
      toast.success(`${data.created} eklendi, ${data.updated} güncellendi${extra}`);
      loadAll();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "İçe aktarma başarısız"); }
  };

  const filterRows = (rows, fields) => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => fields.some((f) => String(r[f] ?? "").toLowerCase().includes(q)));
  };

  const byView = (rows) => rows.filter((r) => (view === "archived" ? r.is_active === false : r.is_active !== false));
  const filteredCustomers = useMemo(
    () => byView(filterRows(customers, ["company_name", "customer_code", "contact_name", "phone", "email"])),
    [customers, query, tab, view]);
  const filteredProducts = useMemo(
    () => byView(filterRows(products, ["product_code", "product_name", "category"])),
    [products, query, tab, view]);
  const filteredUsers = useMemo(
    () => filterRows(users, ["full_name", "username", "email", "role"]),
    [users, query, tab]);

  const archivedCount = (tab === "customers" ? customers : products).filter((r) => r.is_active === false).length;
  const seg = (active) => `min-h-[40px] rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${active ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`;

  return (
    <div>
      <h1 className="mb-1 font-display text-3xl font-black tracking-tight">Müşteri & Ürün Yönetimi</h1>
      <p className="mb-5 text-sm text-muted-foreground">Kataloglar ve sistem kullanıcıları</p>

      <div className="mb-5 flex flex-wrap gap-2">
        {visibleTabs.map((t) => (
          <button key={t.id} data-testid={`mgmt-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex min-h-[44px] items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition-colors ${
              tab === t.id ? "border-primary bg-primary text-white" : "border-border bg-white hover:bg-secondary"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab !== "permissions" && tab !== "company" && (
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          {view === "active" && (
            <button data-testid="mgmt-add-btn" onClick={() => setModal({ type: tab, data: null })}
              className="flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-primary/30 hover:brightness-110">
              <Plus className="h-4 w-4" /> Yeni Ekle
            </button>
          )}
          {tab !== "users" && (
            <div className="inline-flex rounded-xl border border-border bg-white p-1">
              <button data-testid="mgmt-view-active" onClick={() => setView("active")} className={seg(view === "active")}>Aktif</button>
              <button data-testid="mgmt-view-archived" onClick={() => setView("archived")} className={seg(view === "archived")}>
                Arşiv{archivedCount > 0 ? ` (${archivedCount})` : ""}
              </button>
            </div>
          )}
          {(tab === "customers" || tab === "products") && (
            <>
              <button data-testid="mgmt-export-btn" onClick={exportCSV}
                className="flex min-h-[44px] items-center gap-1.5 rounded-xl border border-border bg-white px-3.5 py-2.5 text-sm font-semibold hover:bg-secondary">
                <Download className="h-4 w-4" /> Dışa Aktar
              </button>
              <button data-testid="mgmt-template-btn" onClick={downloadTemplate}
                className="flex min-h-[44px] items-center gap-1.5 rounded-xl border border-border bg-white px-3.5 py-2.5 text-sm font-semibold hover:bg-secondary">
                <FileSpreadsheet className="h-4 w-4" /> Örnek Şablon
              </button>
              <button data-testid="mgmt-import-btn" onClick={() => fileRef.current?.click()}
                className="flex min-h-[44px] items-center gap-1.5 rounded-xl border border-border bg-white px-3.5 py-2.5 text-sm font-semibold hover:bg-secondary">
                <Upload className="h-4 w-4" /> İçeri Aktar
              </button>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,text/csv" onChange={onImportFile} className="hidden" data-testid="mgmt-import-input" />
            </>
          )}
        </div>
        <div className="relative sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="mgmt-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Ara..." autoComplete="off"
            className="min-h-[44px] w-full rounded-xl border border-border bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-primary" />
        </div>
      </div>
      )}

      {tab === "customers" && (
        <List items={filteredCustomers} cols={["Kod", "Firma", "İletişim", "Telefon", "Durum"]}
          render={(c) => [c.customer_code || "—", c.company_name, c.contact_name || "—", c.phone, <StatusPill key="s" active={c.is_active} />]}
          onEdit={view === "active" ? (c) => setModal({ type: "customers", data: c }) : undefined}
          onDelete={view === "active" ? (c) => setConfirm({ type: "customers", item: c }) : undefined}
          onReactivate={view === "archived" ? (c) => reactivate(c) : undefined}
          emptyText={view === "archived" ? "Arşivde müşteri yok" : "Kayıt yok"}
          testprefix="customer" idKey="company_name" />
      )}
      {tab === "products" && (
        <List items={filteredProducts} cols={["Kod", "Ürün Adı", "Kategori", "Birim", "Durum"]}
          render={(p) => [p.product_code, p.product_name, p.category || "—", p.unit, <StatusPill key="s" active={p.is_active} />]}
          onEdit={view === "active" ? (p) => setModal({ type: "products", data: p }) : undefined}
          onDelete={view === "active" ? (p) => setConfirm({ type: "products", item: p }) : undefined}
          onReactivate={view === "archived" ? (p) => reactivate(p) : undefined}
          emptyText={view === "archived" ? "Arşivde ürün yok" : "Kayıt yok"}
          testprefix="product" idKey="product_code" />
      )}
      {tab === "users" && user?.role === "admin" && (
        <List items={filteredUsers} cols={["Ad Soyad", "Kullanıcı Adı", "E-posta", "Rol", "Durum"]}
          render={(u) => [u.full_name, u.username || "—", u.email, ROLE_LABELS[u.role] || u.role, <StatusPill key="s" active={u.is_active} />]}
          onEdit={(u) => setModal({ type: "users", data: u })}
          onDelete={(u) => setConfirm({ type: "users", item: u })}
          testprefix="user" idKey="email" />
      )}

      {tab === "permissions" && user?.role === "admin" && <PermissionsManager />}
      {tab === "company" && user?.role === "admin" && <CompanySettings />}

      {modal && <EditModal modal={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); loadAll(); }} />}
      {confirm && <ConfirmDelete confirm={confirm} onClose={() => setConfirm(null)} onConfirm={doDelete} />}
    </div>
  );
}

function StatusPill({ active }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
      active ? "bg-emerald-500/15 text-emerald-600" : "bg-rose-500/15 text-rose-500"}`}>
      {active ? "Aktif" : "Pasif"}
    </span>
  );
}

function List({ items, cols, render, onEdit, onDelete, onReactivate, testprefix, idKey, emptyText = "Kayıt yok" }) {
  const hasActions = onEdit || onDelete || onReactivate;
  const actionButtons = (it, mobile) => {
    const p = mobile ? "m-" : "";
    const extra = mobile ? "flex-1 justify-center" : "";
    return (
      <>
        {onReactivate && (
          <button data-testid={`${p}${testprefix}-reactivate-${it[idKey]}`} onClick={() => onReactivate(it)}
            className={`inline-flex min-h-[40px] items-center gap-1 rounded-lg border border-emerald-300 px-3 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-50 ${extra}`}>
            <RotateCcw className="h-3.5 w-3.5" /> Yeniden Aktifleştir
          </button>
        )}
        {onEdit && (
          <button data-testid={`${p}${testprefix}-edit-${it[idKey]}`} onClick={() => onEdit(it)}
            className={`inline-flex min-h-[40px] items-center gap-1 rounded-lg border border-border px-3 py-2 text-xs font-medium hover:bg-secondary ${extra}`}>
            <Pencil className="h-3.5 w-3.5" /> Düzenle
          </button>
        )}
        {onDelete && (
          <button data-testid={`${p}${testprefix}-delete-${it[idKey]}`} onClick={() => onDelete(it)}
            className={`inline-flex min-h-[40px] items-center gap-1 rounded-lg border border-rose-300 px-3 py-2 text-xs font-medium text-rose-600 hover:bg-rose-50 ${extra}`}>
            <Trash2 className="h-3.5 w-3.5" /> Sil
          </button>
        )}
      </>
    );
  };
  return (
    <>
      {/* Mobile: stacked cards */}
      <div className="space-y-3 md:hidden">
        {items.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border py-10 text-center text-muted-foreground">{emptyText}</div>
        )}
        {items.map((it) => {
          const vals = render(it);
          const last = vals.length - 1;
          return (
            <div key={it.id} data-testid={`m-${testprefix}-row-${it[idKey]}`}
              className={`rounded-2xl border border-border bg-white p-4 shadow-sm ${it.is_active === false ? "opacity-60" : ""}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-mono text-sm font-bold text-primary break-words">{vals[0]}</div>
                  <div className="mt-0.5 font-semibold break-words">{vals[1]}</div>
                </div>
                <div className="shrink-0">{vals[last]}</div>
              </div>
              <div className="mt-3 space-y-1.5 text-sm">
                {cols.slice(2, last).map((c, i) => (
                  <div key={c} className="flex items-start justify-between gap-3">
                    <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{c}</span>
                    <span className="min-w-0 break-words text-right">{vals[i + 2]}</span>
                  </div>
                ))}
              </div>
              {hasActions && (
                <div className="mt-3 flex flex-wrap gap-2 border-t border-border pt-3">{actionButtons(it, true)}</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Desktop: table */}
      <div className="hidden overflow-x-auto rounded-2xl border border-border bg-white shadow-sm md:block">
        <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-secondary/50">
            {cols.map((c) => <th key={c} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-widest text-muted-foreground">{c}</th>)}
            {hasActions && <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-widest text-muted-foreground">İşlemler</th>}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={cols.length + 1} className="px-4 py-10 text-center text-muted-foreground">{emptyText}</td></tr>}
          {items.map((it) => (
            <tr key={it.id} data-testid={`${testprefix}-row-${it[idKey]}`} className={`border-b border-border/60 last:border-0 hover:bg-secondary/30 ${it.is_active === false ? "opacity-60" : ""}`}>
              {render(it).map((val, i) => (
                <td key={i} className={`px-4 py-3 ${i === 0 ? "font-mono font-semibold" : ""}`}>{val}</td>
              ))}
              {hasActions && (
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    {onReactivate && (
                      <button data-testid={`${testprefix}-reactivate-${it[idKey]}`} onClick={() => onReactivate(it)}
                        className="inline-flex min-h-[40px] items-center gap-1 rounded-lg border border-emerald-300 px-3 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-50">
                        <RotateCcw className="h-3.5 w-3.5" /> Yeniden Aktifleştir
                      </button>
                    )}
                    {onEdit && (
                      <button data-testid={`${testprefix}-edit-${it[idKey]}`} onClick={() => onEdit(it)}
                        className="inline-flex min-h-[40px] items-center gap-1 rounded-lg border border-border px-3 py-2 text-xs font-medium hover:bg-secondary">
                        <Pencil className="h-3.5 w-3.5" /> Düzenle
                      </button>
                    )}
                    {onDelete && (
                      <button data-testid={`${testprefix}-delete-${it[idKey]}`} onClick={() => onDelete(it)}
                        className="inline-flex min-h-[40px] items-center gap-1 rounded-lg border border-rose-300 px-3 py-2 text-xs font-medium text-rose-600 hover:bg-rose-50">
                        <Trash2 className="h-3.5 w-3.5" /> Sil
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </>
  );
}

function ConfirmDelete({ confirm, onClose, onConfirm }) {
  const { type, item } = confirm;
  const [busy, setBusy] = useState(false);
  const label = type === "customers" ? (item.company_name) : type === "products" ? `${item.product_code} – ${item.product_name}` : item.full_name;
  const hint = type === "users"
    ? "Kullanıcı kalıcı olarak silinecek. Son yönetici hesabı silinemez."
    : "Bu kayıt siparişlerde kullanıldıysa geçmiş korunur ve otomatik olarak arşivlenir (pasife alınır); kullanılmadıysa kalıcı olarak silinir.";
  const run = async () => { setBusy(true); await onConfirm(); setBusy(false); };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="delete-confirm-modal">
      <div className="w-full max-w-md rounded-2xl border border-border bg-white p-5 shadow-2xl">
        <div className="mb-3 flex items-start gap-3">
          <div className="rounded-xl bg-rose-500/15 p-2 text-rose-600"><AlertTriangle className="h-5 w-5" /></div>
          <div>
            <h3 className="font-display text-lg font-bold">Silme Onayı</h3>
            <p className="mt-1 text-sm text-muted-foreground"><span className="font-semibold text-foreground">{label}</span> kaydını silmek istediğinize emin misiniz?</p>
          </div>
        </div>
        <p className="mb-4 rounded-xl bg-secondary/60 px-3 py-2.5 text-xs text-muted-foreground">{hint}</p>
        <div className="flex gap-2">
          <button data-testid="delete-cancel-btn" onClick={onClose} className="min-h-[44px] flex-1 rounded-xl border border-border py-3 text-sm font-semibold hover:bg-secondary">Vazgeç</button>
          <button data-testid="delete-confirm-btn" onClick={run} disabled={busy} className="min-h-[44px] flex-1 rounded-xl bg-rose-600 py-3 text-sm font-semibold text-white hover:brightness-110 disabled:opacity-50">
            {busy ? "İşleniyor..." : "Evet, Sil"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditModal({ modal, onClose, onSaved }) {
  const { type, data } = modal;
  const isEdit = !!data;
  const defaults = {
    customers: { company_name: "", customer_code: "", contact_name: "", phone: "", email: "", address: "", note: "", tax_no: "", qr_link: "", is_active: true },
    products: { product_code: "", product_name: "", category: "", unit: "Litre", description: "", is_active: true },
    users: { full_name: "", username: "", email: "", phone: "", role: "staff", password: "", is_active: true },
  };
  const [form, setForm] = useState({ ...defaults[type], ...(data || {}) });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (type === "customers") {
        isEdit ? await api.put(`/customers/${data.id}`, form) : await api.post("/customers", form);
      } else if (type === "products") {
        isEdit ? await api.put(`/products/${data.id}`, form) : await api.post("/products", form);
      } else if (type === "users") {
        if (isEdit) {
          const payload = { full_name: form.full_name, username: form.username, email: form.email, phone: form.phone, role: form.role, is_active: form.is_active };
          if (form.password) payload.password = form.password;
          await api.put(`/users/${data.id}`, payload);
        } else {
          await api.post("/users", form);
        }
      }
      toast.success("Kaydedildi");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const ic = "min-h-[44px] w-full rounded-xl border border-border bg-white px-3.5 py-2.5 text-sm outline-none focus:border-primary";
  const lc = "mb-1 block text-xs font-semibold text-muted-foreground";
  const titleMap = { customers: "Müşteri", products: "Ürün", users: "Kullanıcı" };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="mgmt-modal">
      <div className="w-full max-w-md rounded-2xl border border-border bg-white p-5 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold">{isEdit ? "Düzenle" : "Yeni"} · {titleMap[type]}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>

        <div className="max-h-[62vh] space-y-3 overflow-y-auto pr-1">
          {type === "customers" && (<>
            <div><label className={lc}>Firma Adı *</label><input data-testid="field-company_name" className={ic} value={form.company_name} onChange={(e) => set("company_name", e.target.value)} /></div>
            <div><label className={lc}>Müşteri Kodu</label><input data-testid="field-customer_code" className={`${ic} font-mono`} value={form.customer_code} onChange={(e) => set("customer_code", e.target.value)} /></div>
            <div><label className={lc}>Yetkili Kişi</label><input className={ic} value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} /></div>
            <div><label className={lc}>Telefon *</label><input data-testid="field-phone" className={ic} value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            <div><label className={lc}>E-posta</label><input className={ic} value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div><label className={lc}>Adres</label><textarea className={ic} rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
            <div><label className={lc}>Açıklama</label><textarea className={ic} rows={2} value={form.note} onChange={(e) => set("note", e.target.value)} /></div>
            <div><label className={lc}>Vergi No</label><input data-testid="field-tax_no" className={ic} value={form.tax_no} onChange={(e) => set("tax_no", e.target.value)} /></div>
            <div>
              <label className={lc}>Kare Kod Link (QR)</label>
              <input data-testid="field-qr_link" className={ic} value={form.qr_link} onChange={(e) => set("qr_link", e.target.value)} placeholder="https://..." />
              {form.qr_link ? (
                <div className="mt-3 flex items-center gap-3 rounded-xl border border-border bg-secondary/30 p-3">
                  <div className="rounded-lg bg-white p-2" data-testid="qr-preview"><QRCodeSVG value={form.qr_link} size={96} /></div>
                  <span className="break-all text-xs text-muted-foreground">{form.qr_link}</span>
                </div>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground">Bir link girince kare kod (QR) önizlemesi burada görünür.</p>
              )}
            </div>
            <ActiveToggle value={form.is_active} onChange={(v) => set("is_active", v)} />
          </>)}
          {type === "products" && (<>
            <div><label className={lc}>Ürün Kodu *</label><input data-testid="field-product_code" className={`${ic} font-mono`} value={form.product_code} onChange={(e) => set("product_code", e.target.value)} /></div>
            <div><label className={lc}>Ürün Adı *</label><input data-testid="field-product_name" className={ic} value={form.product_name} onChange={(e) => set("product_name", e.target.value)} /></div>
            <div><label className={lc}>Kategori</label><select data-testid="field-category" className={ic} value={form.category} onChange={(e) => set("category", e.target.value)}><option value="">Seçiniz…</option>{PRODUCT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
            <div><label className={lc}>Birim</label><select className={ic} value={form.unit} onChange={(e) => set("unit", e.target.value)}>{UNITS.map((u) => <option key={u}>{u}</option>)}</select></div>
            <div><label className={lc}>Açıklama</label><textarea className={ic} rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
            <ActiveToggle value={form.is_active} onChange={(v) => set("is_active", v)} />
          </>)}
          {type === "users" && (<>
            <div><label className={lc}>Ad Soyad *</label><input data-testid="field-full_name" className={ic} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} /></div>
            <div><label className={lc}>Kullanıcı Adı</label><input data-testid="field-username" className={ic} value={form.username} onChange={(e) => set("username", e.target.value)} /></div>
            <div><label className={lc}>E-posta *</label><input data-testid="field-email" type="email" className={ic} value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div><label className={lc}>Telefon</label><input className={ic} value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            <div><label className={lc}>Rol</label><select data-testid="field-role" className={ic} value={form.role} onChange={(e) => set("role", e.target.value)}>
              <option value="staff">Sipariş Personeli</option><option value="production">Üretim</option><option value="admin">Yönetici</option></select></div>
            <div><label className={lc}>Şifre {isEdit ? "(değiştirmek için doldurun)" : "*"}</label><input data-testid="field-password" type="password" placeholder={isEdit ? "Boş bırakılırsa değişmez" : ""} className={ic} value={form.password} onChange={(e) => set("password", e.target.value)} /></div>
            {isEdit && <ActiveToggle value={form.is_active} onChange={(v) => set("is_active", v)} label="Hesap Aktif" />}
          </>)}
        </div>

        <button data-testid="mgmt-save-btn" onClick={save} disabled={saving} className="mt-4 min-h-[48px] w-full rounded-xl bg-primary py-3 font-semibold text-white hover:brightness-110 disabled:opacity-50">
          {saving ? "Kaydediliyor..." : "Kaydet"}
        </button>
      </div>
    </div>
  );
}

function ActiveToggle({ value, onChange, label = "Aktif" }) {
  return (
    <label className="flex cursor-pointer items-center justify-between rounded-xl border border-border px-3.5 py-3">
      <span className="text-sm font-semibold text-muted-foreground">{label}</span>
      <button type="button" data-testid="field-is_active" onClick={() => onChange(!value)}
        className={`relative h-6 w-11 rounded-full transition-colors ${value ? "bg-primary" : "bg-slate-300"}`}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${value ? "left-[22px]" : "left-0.5"}`} />
      </button>
    </label>
  );
}
